import { create } from 'zustand';
import { AxiosError } from 'axios';
import { getCartApi } from '@/service/api/Cart/getCartApi';
import { addCartApi } from '@/service/api/Cart/addCartApi';
import { extCartApi } from '@/service/api/Cart/extCartApi';
import { delCartApi } from '@/service/api/Cart/delCartApi';
import { getCartPromoApi } from '@/service/api/Cart/getCartPromoApi';
import { addCartPromoApi } from '@/service/api/Cart/addCartPromoApi';
import { delCartPromoApi } from '@/service/api/Cart/delCartPromoApi';
import { CART_ID } from '@/service/constants/variables';
import { ECartActionType, TCartStore } from './domain';
import { DEFAULT_CART_STATE } from './state';
import { useAvailabilityStore } from '../Availability/useAvailabilityStore';
import { getTipsApi } from '@/service/api/Tips/getTipsApi';
import { scrollToTop } from '@/service/utils/scrollToId';
import { trackAddToCart } from '@/service/GA4/ga4Events';
import { getLSwithTime, removeLS, setLSwithTime } from '@/service/storage/LocalStorage';
import { trackFBPixelEvent } from '@/service/FBpixel/FBpixel';

export const useCartStore = create<TCartStore>((set, get) => ({
    ...DEFAULT_CART_STATE,
    getCart: async ({ partnerId }, actionType, newItemIds) => {
        set({ loading: { actionType, state: true } });
        try {
            const id = getLSwithTime(CART_ID) ?? get().cart_id;
            if (!id) throw new Error('No cart ID found');
            const cart = await getCartApi({ id, partnerId });

            if (cart?.time_left && cart.time_left > 0) {
                set({
                    loading: { actionType, state: false },
                    cart,
                    cart_id: cart.cart_id,
                    error: { actionType, message: '' },
                    success: { actionType, message: 'Get cart successfully' },
                });

                const newItems =
                    !!newItemIds?.length &&
                    cart.cart_items.filter(({ id }) => newItemIds.includes(id));

                if (!!newItems && newItems.length) {
                    trackAddToCart({
                        currency: 'USD',
                        value: cart.grand_total,
                        items: newItems.map((newItem) => ({
                            item_id: newItem.product_id,
                            item_name: newItem.product_name,
                            affiliation: newItem.partner_title,
                            item_brand: newItem.partner_title,
                            item_category:
                                typeof newItem.trip_type != 'undefined' && newItem.trip_type === '1'
                                    ? 'Tours'
                                    : 'Rentals',
                            price: newItem.subtotal,
                            quantity: 1,
                            item_variant: newItem.booked_format,
                        })),
                    });

                    trackFBPixelEvent('AddToCart', {
                        content_ids: newItems.map(({ product_id }) => product_id),
                        content_type: 'product',
                        currency: 'USD',
                        value: cart.grand_total,
                        contents: newItems.map(({ product_id }) => ({
                            id: product_id,
                            quantity: 1,
                        })),
                    });
                }

                return cart;
            } else {
                set({
                    loading: { actionType, state: false },
                    cart: null,
                    error: { actionType, message: '' },
                    success: { actionType, message: '' },
                });

                get().setEmptyCart();

                return null;
            }
        } catch (e) {
            set({ loading: { actionType: ECartActionType.GET_CART, state: false } });
            if ((e as Error).message === 'Request failed with status code 501') {
                removeLS(CART_ID);
                set({ cart: null });
            } else {
                set({ cart: get().cart });
            }
            set({
                error: {
                    actionType: ECartActionType.GET_CART,
                    message: `Failed to get cart: ${(e as Error).message}`,
                },
            });
            set({ success: { actionType: ECartActionType.GET_CART, message: '' } });
        }

        return null;
    },
    addCart: async ({ partnerId }, body, showToast) => {
        set({ loading: { actionType: ECartActionType.ADD_ITEM_TO_CART, state: true } });
        try {
            const id = get().cart_id ?? '0';
            const addedCart = await addCartApi({ id, partnerId }, body);
            setLSwithTime(CART_ID, addedCart.cart_id, 1800);
            set({
                loading: { actionType: ECartActionType.ADD_ITEM_TO_CART, state: false },
                error: { actionType: ECartActionType.ADD_ITEM_TO_CART, message: '' },
                success: {
                    actionType: ECartActionType.ADD_ITEM_TO_CART,
                    message: showToast ? `Added item to the cart successfully` : '',
                },
                cart_id: addedCart.cart_id,
            });
            get().getCart(
                { partnerId },
                ECartActionType.GET_CART,
                addedCart.new_items.map((id) => id.toString())
            );

            return {
                success: {
                    actionType: ECartActionType.ADD_ITEM_TO_CART,
                    message: `Added item to the cart successfully`,
                },
            };
        } catch (e) {
            const errorMsg =
                (e as AxiosError<{ message: string }>)?.response?.data?.message ||
                (e as Error).message;

            set({ loading: { actionType: ECartActionType.ADD_ITEM_TO_CART, state: false } });
            set({
                error: {
                    actionType: ECartActionType.ADD_ITEM_TO_CART,
                    message: `Failed to add item to the cart: ${errorMsg}`,
                },
            });
            set({ success: { actionType: ECartActionType.ADD_ITEM_TO_CART, message: '' } });

            if (errorMsg) {
                useAvailabilityStore.getState().setErrorMessage(errorMsg);
            }
            return { success: { actionType: ECartActionType.ADD_ITEM_TO_CART, message: '' } };
        }
    },
    extCart: async ({ partnerId }) => {
        set({ loading: { actionType: ECartActionType.EXTEND_TIME_FOR_CART, state: true } });
        try {
            const id = getLSwithTime(CART_ID) ?? get().cart_id;
            if (!id) throw new Error('No cart ID found');
            await extCartApi({ id, partnerId });
            set({ loading: { actionType: ECartActionType.EXTEND_TIME_FOR_CART, state: false } });
            set({ error: { actionType: ECartActionType.EXTEND_TIME_FOR_CART, message: '' } });
            set({
                success: {
                    actionType: ECartActionType.EXTEND_TIME_FOR_CART,
                    message: 'The cart has been extended successfully',
                },
            });
            get().getCart({ partnerId }, ECartActionType.GET_CART);
        } catch (e) {
            set({ loading: { actionType: ECartActionType.EXTEND_TIME_FOR_CART, state: false } });
            set({
                error: {
                    actionType: ECartActionType.EXTEND_TIME_FOR_CART,
                    message: `Failed to extend time for the cart: ${(e as Error).message}`,
                },
            });
            set({ success: { actionType: ECartActionType.EXTEND_TIME_FOR_CART, message: '' } });
        }
    },
    delCart: async ({ partnerId, item_id }) => {
        set({ loading: { actionType: ECartActionType.DELETE_ITEM_FROM_CART, state: true } });
        try {
            const id = getLSwithTime(CART_ID) ?? get().cart_id;
            if (!id) throw new Error('No cart ID found');
            await delCartApi({ id, partnerId, item_id });
            set({ loading: { actionType: ECartActionType.DELETE_ITEM_FROM_CART, state: false } });
            set({ error: { actionType: ECartActionType.DELETE_ITEM_FROM_CART, message: '' } });
            set({
                success: {
                    actionType: ECartActionType.DELETE_ITEM_FROM_CART,
                    message: `Item removed from the cart successfully`,
                },
            });
            set({ tips: 0 });

            get().getCart({ partnerId }, ECartActionType.GET_CART);
        } catch (e) {
            set({ loading: { actionType: ECartActionType.DELETE_ITEM_FROM_CART, state: false } });
            set({
                error: {
                    actionType: ECartActionType.DELETE_ITEM_FROM_CART,
                    message: `Failed to delete an item from the cart: ${(e as Error).message}`,
                },
            });
            set({ success: { actionType: ECartActionType.DELETE_ITEM_FROM_CART, message: '' } });
        }
    },
    clearError: (actionType) => setTimeout(() => set({ error: { actionType, message: '' } }), 500),
    clearSuccess: (actionType) =>
        setTimeout(() => set({ success: { actionType, message: '' } }), 500),
    setEmptyCart: () => {
        removeLS(CART_ID);
        set(DEFAULT_CART_STATE);
    },
    handleRemovedCartItemsChange: (cartItemId) => {
        const removedItem = get().cart?.cart_items.find((item) => item.id === cartItemId);
        if (removedItem) set({ removedCartItems: [...get().removedCartItems, removedItem] });
    },
    resetRemovedCartItems: () => set({ removedCartItems: DEFAULT_CART_STATE.removedCartItems }),
    setExpiredCartItems: (expiredCartItems) => set({ expiredCartItems }),
    getCartPromo: async ({ partnerId }, actionType) => {
        set({ loading: { actionType, state: true } });
        try {
            const id = getLSwithTime(CART_ID) ?? get().cart_id;
            if (!id) throw new Error('No cart ID found');
            const promoCode = await getCartPromoApi({ id, partnerId });
            set({ loading: { actionType, state: false } });
            set({ error: { actionType, message: '' } });
            set({ success: { actionType, message: 'Get cart promo code successfully' } });
            set({
                cart: { ...get().cart!, discount: { ...get().cart!.discount, promo: promoCode } },
            });
        } catch (e) {
            set({ loading: { actionType, state: false } });
            set({
                error: {
                    actionType,
                    message: `Failed to get cart promo code: ${(e as Error).message}`,
                },
            });
            set({ success: { actionType, message: '' } });
            set({ cart: get().cart! });
        }
    },
    addCartPromo: async ({ partnerId }, body) => {
        scrollToTop();
        set({ loading: { actionType: ECartActionType.ADD_PROMO_CODE_TO_CART, state: true } });
        try {
            const id = getLSwithTime(CART_ID) ?? get().cart_id;
            if (!id) throw new Error('No cart ID found');
            await addCartPromoApi({ id, partnerId }, body);
            set({ loading: { actionType: ECartActionType.ADD_PROMO_CODE_TO_CART, state: false } });
            set({ error: { actionType: ECartActionType.ADD_PROMO_CODE_TO_CART, message: '' } });
            set({
                success: {
                    actionType: ECartActionType.ADD_PROMO_CODE_TO_CART,
                    message: `Added promo code to the cart successfully`,
                },
            });
            get().getCart({ partnerId }, ECartActionType.ADD_PROMO_CODE_TO_CART);
        } catch (e) {
            const errorMsg =
                (e as AxiosError<{ message: string }>)?.response?.data?.message ||
                (e as Error).message;

            set({ loading: { actionType: ECartActionType.ADD_PROMO_CODE_TO_CART, state: false } });
            set({
                error: {
                    actionType: ECartActionType.ADD_PROMO_CODE_TO_CART,
                    message: `Failed to add promo code to the cart: ${errorMsg}`,
                },
            });
            set({ success: { actionType: ECartActionType.ADD_PROMO_CODE_TO_CART, message: '' } });
        }
    },
    delCartPromo: async ({ partnerId }) => {
        scrollToTop();
        set({ loading: { actionType: ECartActionType.DELETE_PROMO_CODE_FROM_CART, state: true } });
        try {
            const id = getLSwithTime(CART_ID) ?? get().cart_id;
            if (!id) throw new Error('No cart ID found');
            await delCartPromoApi({ id, partnerId });
            set({
                loading: { actionType: ECartActionType.DELETE_PROMO_CODE_FROM_CART, state: false },
            });
            set({
                error: { actionType: ECartActionType.DELETE_PROMO_CODE_FROM_CART, message: '' },
            });
            set({
                success: {
                    actionType: ECartActionType.DELETE_PROMO_CODE_FROM_CART,
                    message: `Promo code removed from the cart successfully`,
                },
            });
            get().getCart({ partnerId }, ECartActionType.DELETE_PROMO_CODE_FROM_CART);
        } catch (e) {
            set({
                loading: { actionType: ECartActionType.DELETE_PROMO_CODE_FROM_CART, state: false },
            });
            set({
                error: {
                    actionType: ECartActionType.DELETE_PROMO_CODE_FROM_CART,
                    message: `Failed to remove the promo code from the cart: ${
                        (e as Error).message
                    }`,
                },
            });
            set({
                success: { actionType: ECartActionType.DELETE_PROMO_CODE_FROM_CART, message: '' },
            });
        }
    },
    getTips: async (partnerId) => {
        try {
            const id = getLSwithTime(CART_ID) ?? get().cart_id;

            if (!id) throw new Error('No cart ID found');

            const tips = await getTipsApi({ id, partnerId });

            set({ tips: tips.total });
        } catch (e) {
            set({
                error: {
                    actionType: ECartActionType.GET_TIPS,
                    message: `Failed getting tips: ${(e as Error).message}`,
                },
            });
        }
    },
    showThenHideCartToast: () => {
        set({ cartToastIsShown: true });
        setTimeout(() => set({ cartToastIsShown: false }), 3000);
    },
}));
